import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, User, Share2 } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const { data, error } = await supabase
          .from("blog_posts")
          .select("*")
          .eq("slug", slug)
          .single();
        
        if (!error && data) {
          setPost(data);
        }
      } catch (error) {
        console.error("Failed to fetch post:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: url,
        });
      } catch {
        // User cancelled or error
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-[#808080]">Loading...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen px-4 py-16 max-w-screen-xl mx-auto text-center">
        <h1 className="text-3xl font-rotten text-[#d0d0d0] mb-4">Post Not Found</h1>
        <p className="text-[#808080] mb-8">This blog post doesn&apos;t exist or has been unpublished.</p>
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 neuro-base rounded-xl px-6 py-3 text-[#a0a0a0] hover:scale-105 transition-transform"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 py-8 max-w-3xl mx-auto">
      {/* Back Link */}
      <Link
        to="/blog"
        className="inline-flex items-center gap-2 text-[#808080] hover:text-[#a0a0a0] transition-colors mb-8"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Blog
      </Link>

      {/* Article Header */}
      <article className="neuro-base rounded-3xl p-6 md:p-10">
        <header className="mb-8 pb-6 border-b border-[#2a2a2a]">
          {!post.published && (
            <div className="mb-4 px-3 py-2 bg-yellow-500/20 border border-yellow-500/30 rounded-lg text-yellow-400 text-sm">
              Draft - This post is not published yet
            </div>
          )}
          <h1 className="text-3xl md:text-4xl font-rotten text-[#d0d0d0] mb-4">
            {post.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-4 text-sm text-[#808080]">
            <span className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {post.author}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {format(new Date(post.created_at), "MMMM d, yyyy")}
            </span>
            <button
              onClick={handleShare}
              className="flex items-center gap-2 hover:text-[#a0a0a0] transition-colors ml-auto"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
          </div>
        </header>

        {/* Article Content */}
        <div className="prose prose-invert max-w-none">
          <div className="text-[#c0c0c0] leading-relaxed whitespace-pre-wrap">
            {post.content}
          </div>
        </div>

        {/* Updated timestamp */}
        {post.updated_at !== post.created_at && (
          <p className="mt-8 pt-4 border-t border-[#2a2a2a] text-xs text-[#606060]">
            Last updated: {format(new Date(post.updated_at), "MMMM d, yyyy 'at' h:mm a")}
          </p>
        )}
      </article>
    </div>
  );
}
